import { useState } from 'react'
import { UserPlus } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Can } from '@/features/auth'
import { useOrgMembers } from './hooks/useOrgMembers'
import { UsersTable } from './components/UsersTable'
import { UsersTableSkeleton } from './components/UsersTableSkeleton'
import { UsersEmptyState } from './components/UsersEmptyState'
import { InviteUserDialog } from './components/InviteUserDialog'
import { ChangeRoleDialog } from './components/ChangeRoleDialog'
import { RemoveRoleDialog } from './components/RemoveRoleDialog'
import { DeactivateUserDialog } from './components/DeactivateUserDialog'
import type { OrgMember } from './api/operations'

// Equipos de organización: listado acotado en una página (sin paginación en la
// v1; el backend limita server-side). Si un equipo crece más, se pagina.
const PAGE_SIZE = 100

/**
 * "Equipo" (goal portal-roles-2): el dueño gestiona los roles reales de su
 * organización — invita con rol, cambia entre Gestor/Contable y QUITA el rol
 * (el miembro conserva la membresía pero la matriz le cierra los datos). Monta
 * la UI de `features/users/` que nació huérfana en el goal 1. No confundir con
 * `/app/users`, el CRM de staff del landing.
 *
 * goal portal-correcciones-1: el listado trae SOLO a quien tiene rol de portal
 * (el filtro es server-side, en el `where` de Identidad). Quien está en la
 * organización sin rol no aparece; se le da uno invitándolo por su correo, que
 * al miembro existente solo le asigna el rol (no lo re-registra).
 */
export function TeamPage() {
  const { members, loading, error, refetch } = useOrgMembers({
    take: PAGE_SIZE,
    skip: 0,
  })

  const [inviteOpen, setInviteOpen] = useState(false)
  const [changingRole, setChangingRole] = useState<OrgMember | null>(null)
  const [removingRole, setRemovingRole] = useState<OrgMember | null>(null)
  const [deactivating, setDeactivating] = useState<OrgMember | null>(null)

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-serif text-[36px] font-normal leading-[1.1] tracking-tight text-ink">
            Equipo
          </h1>
          <p className="mt-1 max-w-xl text-sm text-ink-muted">
            Aquí aparecen los miembros de tu organización que tienen un rol de
            portal. <strong>Gestor</strong>: opera negocios y servicios.{' '}
            <strong>Contable</strong>: facturación y reportes. Para sumar a alguien
            —esté o no ya en tu organización— usa <strong>Invitar miembro</strong> con
            su correo.
          </p>
        </div>
        <Can permission="users:invite">
          <Button variant="primary" onClick={() => setInviteOpen(true)}>
            <UserPlus className="h-4 w-4" />
            Invitar miembro
          </Button>
        </Can>
      </header>

      <div className="rounded-card border border-line bg-panel p-4">
        {loading && members.length === 0 ? (
          <UsersTableSkeleton />
        ) : error ? (
          <div className="flex flex-col items-center gap-3 p-10 text-center">
            <p className="text-sm text-ink-muted">
              No se pudo cargar el equipo. Revisa tu conexión e inténtalo de nuevo.
            </p>
            <Button variant="ghost" onClick={() => void refetch()}>
              Reintentar
            </Button>
          </div>
        ) : members.length === 0 ? (
          <UsersEmptyState />
        ) : (
          <UsersTable
            members={members}
            onChangeRole={setChangingRole}
            onRemoveRole={setRemovingRole}
            onDeactivate={setDeactivating}
          />
        )}
      </div>

      <InviteUserDialog open={inviteOpen} onOpenChange={setInviteOpen} />
      <ChangeRoleDialog
        member={changingRole}
        onOpenChange={(open) => !open && setChangingRole(null)}
      />
      <RemoveRoleDialog
        member={removingRole}
        onOpenChange={(open) => !open && setRemovingRole(null)}
      />
      <DeactivateUserDialog
        member={deactivating}
        onOpenChange={(open) => !open && setDeactivating(null)}
      />
    </div>
  )
}
